import { createContext, useContext, useMemo, useState, ReactNode } from 'react';
import { ToolboxStageId, ToolboxState } from './ToolboxContext';

export interface WorkflowTool {
  id: string;
  name: string;
  category: string;
  stage: ToolboxStageId;
}

export interface Workflow {
  id: string;
  name: string;
  createdAt: string;
  tools: WorkflowTool[];
}

interface WorkflowsContextType {
  workflows: Workflow[];
  addWorkflow: (name: string, toolbox: ToolboxState) => Workflow | null;
  removeWorkflow: (workflowId: string) => void;
}

const WorkflowsContext = createContext<WorkflowsContextType | null>(null);

function buildWorkflowTools(toolbox: ToolboxState): WorkflowTool[] {
  const entries = Object.entries(toolbox) as Array<[ToolboxStageId, ToolboxState[keyof ToolboxState]]>;
  return entries
    .filter(([, tool]) => !!tool)
    .map(([stage, tool]) => ({
      id: tool!.id,
      name: tool!.name,
      category: tool!.category,
      stage,
    }));
}

export function WorkflowsProvider({ children }: { children: ReactNode }) {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  // In-session only: workflows reset on refresh by design.

  const addWorkflow = (name: string, toolbox: ToolboxState) => {
    const trimmed = name.trim();
    if (!trimmed) return null;

    const tools = buildWorkflowTools(toolbox);
    if (tools.length === 0) return null;

    const workflow: Workflow = {
      id: `wf-${Date.now()}`,
      name: trimmed,
      createdAt: new Date().toISOString(),
      tools,
    };

    setWorkflows(prev => [workflow, ...prev]);
    return workflow;
  };

  const removeWorkflow = (workflowId: string) => {
    setWorkflows(prev => prev.filter(workflow => workflow.id !== workflowId));
  };

  const value = useMemo(
    () => ({
      workflows,
      addWorkflow,
      removeWorkflow,
    }),
    [workflows]
  );

  return <WorkflowsContext.Provider value={value}>{children}</WorkflowsContext.Provider>;
}

export function useWorkflows(): WorkflowsContextType {
  const context = useContext(WorkflowsContext);
  if (!context) {
    throw new Error('useWorkflows must be used within a WorkflowsProvider.');
  }
  return context;
}
